<template>
  <div class="matrix-editor-task">
    <div class="modal task">
      <div class="modal-close" @click="onClose"></div>
      <div class="custom-modal-header">
        <h2>Spread Sheet</h2>
        <div class="action-buttons-wrapper">
          <button :disabled="isMarkOutputMode" @click="onSelectAll">
            Select All
          </button>
          <button
            :disabled="isDisableClearButton"
            @click="toggleClearAllConfirm"
          >
            Clear All
          </button>
          <button @click="toggleMarkOutPutMode">
            {{ outputModebuttonLabel }}
          </button>
          <button :disabled="isMarkOutputMode" @click="onRecalculate">
            <span v-if="!isRecalculationLoading">Recalculate</span>
            <div v-else class="progress-icon"></div>
          </button>
        </div>
      </div>

      <div
        id="formula-popover-container"
        ref="sheetContainer"
        class="main-layout"
        @mouseleave.prevent="onCellMouseUP()"
        @mouseup.prevent="onCellMouseUP()"
      >
        <div class="bottom-layout">
          <div
            class="content-wrapper"
            :class="{ 'selected-all': isSelectedAllCell }"
          >
            <div class="content-row">
              <div class="content-count-item"></div>
              <div
                v-for="colIndex in this.sheetDimensions.cols"
                :key="`col-${colIndex}`"
                class="alpha-item"
              >
                {{ getColLabel(colIndex - 1) }}
              </div>
            </div>
            <div
              v-for="(rowData, rowIndex) in sheetData"
              :key="rowIndex"
              class="content-row"
            >
              <div class="content-count-item">
                {{ rowIndex + 1 }}
              </div>
              <div
                v-for="(cell, colIndex) in rowData"
                :key="colIndex"
                class="alpha-item"
                @mousedown.prevent="onCellMouseDown(rowIndex, colIndex)"
                @mouseover.prevent="onCellMouseOver(rowIndex, colIndex)"
                :id="`cell-${rowIndex}-${colIndex}`"
              >
                <div class="input-cell">{{ cell.value || cell.formula }}</div>
              </div>
            </div>
          </div>
          <div
            v-for="(boxStyle, i) in selectedBoxesStyle"
            :key="`selectbox-${i}`"
            class="highlight-box"
            :style="boxStyle"
            :class="{ 'output-box': isMarkOutputMode }"
          ></div>
          <div
            v-if="currentRangeBoxStyle"
            class="highlight-box"
            :style="currentRangeBoxStyle"
            :class="{ 'output-box': isMarkOutputMode }"
          />
          <div
            v-if="activeCellBoxStyle && activeCell"
            class="active-cell-box highlight-box"
            :class="{ 'is-focus': activeCell.isFocus }"
            :style="activeCellBoxStyle"
            @dblclick="handleCellDoubleclick"
            @click="handleActiveCellclick"
          >
            <SpreadSheetCell
              v-if="activeCell.isFocus"
              ref="activeCellRef"
              :key="`${activeCellPosition.rowIndex}-${activeCellPosition.colIndex}-${activeCell.value}`"
              :rowIndex="activeCellPosition.rowIndex"
              :colIndex="activeCellPosition.colIndex"
              :cellObject="activeCell"
              :sheetData="sheetData"
              @onInput="handleCellInputEvent"
              @onBlur="handleCellBlurEvent"
              @onFocus="handleCellFocusEvent"
            />
          </div>
        </div>
      </div>

      <b-popover
        v-if="activeCell"
        ref="popoverRef"
        :key="`${activeCellPosition.rowIndex}-${activeCellPosition.colIndex}-${activeCell.value}`"
        :target="`cell-${activeCellPosition.rowIndex}-${activeCellPosition.colIndex}`"
        triggers="click"
        placement="bottom"
        custom-class="formula-popover-dropdown"
        container="formula-popover-container"
        :offset="-150"
      >
        <ul
          :class="{ 'list-view': filteredFunctions.length }"
          v-show="filteredFunctions.length"
        >
          <li
            v-for="func in filteredFunctions"
            :key="func.german"
            @click.stop="onInsertFormulaFunction(func.german)"
          >
            {{ func.german }}
          </li>
        </ul>
      </b-popover>
      <div class="loading-container" v-if="isLoading">
        <LoadingSpinner
          loadingCaption="Wird gespeichert..."
          v-show="isLoading"
        />
      </div>
    </div>
    <CommonModal
      v-if="isShowClearAllConfirm"
      :buttons="confirmModalButtons"
      header="body-message"
      @onConfirm="onClearAllConfirm"
      @close="toggleClearAllConfirm"
    >
      <template v-slot:body-message>
        <p>Möchten Sie wirklich alle Daten löschen?</p>
      </template>
    </CommonModal>
  </div>
</template>

<script>
import {
  getColumnLabel,
  parseRange,
  setCursorAtLast,
  extractCellRefsFromFormula,
  extractCellRefsFromRange,
  getCellHighlightBoxStyle,
  getNavigateCellCoordinate,
  getLastCellReference,
  parseCell,
  isSpecialCharAtEnd,
} from '../../utils/helpers/SpreadSheetHelder';
import SpreadSheetCell from '../widgets/SpreadSheetCell.vue';
import { calculateSpreadSheet } from '@/services/axios/flow-runner.api.js';
import LoadingSpinner from '../widgets/LoadingSpinner.vue';
import CommonModal from '@/components/modals/CommonModal.vue';

export default {
  name: 'SpreadSheet',
  props: {
    task: {
      type: Object,
      required: true,
      default: () => {},
    },
    flowRunnerEndpoint: {
      type: String,
    },
  },
  data() {
    return {
      sheetData: [],
      isLoading: false,
      isRecalculationLoading: false,
      isMarkOutputMode: false,
      isSelectedAllCell: false,
      isShowClearAllConfirm: false,
      isNotExistCellDirect: false,
      isMouseDown: false,
      activeCellPosition: { rowIndex: null, colIndex: null },
      isAllowToAddRefInFormula: false,
      activeCell: null,
      isActiveformula: false,
      keyModifiers: {
        shift: false,
        control: false,
      },
      savedSelectedRange: { start: null, end: null },
      selectedRange: { start: null, end: null },
      selectedOutputCellList: [],
      cellWidth: 120,
      cellHeight: 22,
      activeCellScreenPos: {},
      formulafunctionList: [
        { german: 'SUMME', english: 'SUM' },
        {
          german: 'MITTELWERT',
          english: 'AVERAGE',
        },
        {
          german: 'MEDIAN',
          english: 'MEDIAN',
        },
        {
          german: 'RUNDEN',
          english: 'ROUND',
        },
        {
          german: 'ABS',
          english: 'ABS',
        },
        {
          german: 'ABRUNDEN',
          english: 'ROUNDDOWN',
        },
        {
          german: 'AUFRUNDEN',
          english: 'ROUNDUP',
        },
        {
          german: 'PROZENTVON',
          english: 'PERCENTOF',
        },
      ],
      confirmModalButtons: [
        {
          text: 'löschen',
          action: 'onConfirm',
          loading: false,
        },
        {
          text: 'Abbrechen',
          action: 'close',
          loading: false,
        },
      ],
    };
  },

  methods: {
    initGrid(sheetData) {
      const initialDataList = [];

      for (let i = 0; i < this.sheetDimensions.rows; i++) {
        const rowData = [];
        for (let j = 0; j < this.sheetDimensions.cols; j++) {
          const cellRef = this.getCellRef(i, j);
          const cellData = sheetData
            ? sheetData[cellRef]
            : { value: null, formula: null };
          rowData.push({
            value: cellData?.value || null,
            formula: cellData?.formula || null,
          });
        }
        initialDataList.push(rowData);
      }

      this.sheetData = initialDataList;
    },
    async onClose() {
      try {
        this.isLoading = true;
        await this.onRecalculate();
        const sheetData = this.convertSheetToObject();
        this.task.props.sheetData = sheetData;
        const sheetOutputList = (this.task.props?.resultList || []).map(
          (cellRef) => {
            const cellValue = sheetData?.[cellRef]?.value || '';
            return {
              type: typeof cellValue,
              value: cellValue,
              name: cellRef,
              label: cellRef,
            };
          }
        );
        this.task.output = sheetOutputList || [];
        this.$set(
          this.task,
          'output',
          this.task.output.concat(this.task.input)
        );

        this.$emit('close');
      } catch (error) {
        console.log('');
      } finally {
        this.isLoading = false;
      }
    },
    async onRecalculate() {
      if (this.isRecalculationLoading) return;
      try {
        this.clearActiveCell();
        this.isRecalculationLoading = true;
        const sheetData = this.convertSheetToObject(true);
        if (!sheetData || !Object.keys(sheetData).length) return;
        const result = await calculateSpreadSheet(this.flowRunnerEndpoint, {
          sheetData,
          taskInput: this.task.input,
        });
        if (result.data && Object.keys(result.data)?.length) {
          Object.keys(result.data).forEach((key) => {
            if (result.data[key]) {
              result.data[key].formula = this.convertFormulaLanguage(
                result.data[key].formula,
                'toGerman'
              );
            }
          });
          this.initGrid(result.data);
        }
      } catch (error) {
        console.log('error', error);
        return {};
      } finally {
        this.isRecalculationLoading = false;
      }
    },
    toggleMarkOutPutMode() {
      if (this.isMarkOutputMode) {
        this.saveMarkOutput();
        this.savedSelectedRange = { start: null, end: null };
        this.selectedRange = { start: null, end: null };
        this.selectedOutputCellList = [];
      } else {
        this.selectedOutputCellList = JSON.parse(
          JSON.stringify(this.task?.props?.resultList || [])
        );
        this.clearActiveCell();
        this.unSelectAllCell();
      }
      this.isMarkOutputMode = !this.isMarkOutputMode;
    },
    onSelectAll() {
      if (!this.isMarkOutputMode) this.isSelectedAllCell = true;
      if (!this.isValidActiveCellText) {
        this.selectedOutputCellList = [];
      }
    },
    unSelectAllCell() {
      this.isSelectedAllCell = false;
    },
    toggleClearAllConfirm() {
      this.isShowClearAllConfirm = !this.isShowClearAllConfirm;
    },
    onClearAllConfirm() {
      this.toggleClearAllConfirm();
      if (this.isDisableClearButton) return;
      if (this.isSelectedAllCell) this.initGrid();
      if (this.selectedOutputCellList?.length) {
        const resultList = this.getSelectedCellRefList();
        resultList.forEach((cellRef) => {
          const cellCoordinate = parseCell(cellRef);
          if (cellCoordinate) {
            const cell =
              this.sheetData?.[cellCoordinate.rowIndex]?.[
                cellCoordinate.colIndex
              ];
            if (cell) {
              cell.value = null;
              cell.formula = null;
            }
          }
        });
      }
    },
    saveMarkOutput() {
      const resultlist = this.getSelectedCellRefList();
      this.$set(this.task.props, 'resultList', resultlist);
    },
    getSelectedCellRefList() {
      let selectedRangeList = [];
      let selectedSingleCells = [];
      this.selectedOutputCellList.forEach((cell) => {
        if (cell.includes(':')) {
          selectedRangeList.push(cell);
        } else {
          selectedSingleCells.push(cell);
        }
      });

      const expandedCells = selectedRangeList.flatMap((range) =>
        extractCellRefsFromRange(range)
      );
      const resultlist = [
        ...new Set([...expandedCells, ...selectedSingleCells]),
      ];
      return resultlist;
    },
    convertSheetToObject(isTranslate = false) {
      const result = {};

      for (let row = 0; row < this.sheetData.length; row++) {
        for (let col = 0; col < this.sheetData[row].length; col++) {
          const cellDetails = this.sheetData[row][col];
          if (!(cellDetails.value || cellDetails.formula)) continue;
          cellDetails.value = this.convertToNumber(cellDetails.value);
          const cellRef = this.getCellRef(row, col);
          result[cellRef] = isTranslate
            ? {
                ...cellDetails,
                formula: this.convertFormulaLanguage(cellDetails.formula),
              }
            : cellDetails;
        }
      }
      return result;
    },
    convertToNumber(input) {
      if (typeof input === 'string' || typeof input === 'number') {
        const num = Number(input);
        return !isNaN(num) && input !== '' ? num : input;
      }
      return input;
    },
    getColLabel(index) {
      return getColumnLabel(index);
    },
    getCellRef(rowIndex, colIndex) {
      const colLetter = this.getColLabel(colIndex);
      return `${colLetter}${rowIndex + 1}`;
    },
    getRangeRefs(start, end) {
      if (!start || !end) return null;
      const startRow = Math.min(start.rowIndex, end.rowIndex);
      const endRow = Math.max(start.rowIndex, end.rowIndex);
      const startCol = Math.min(start.colIndex, end.colIndex);
      const endCol = Math.max(start.colIndex, end.colIndex);

      const startCellRef = this.getCellRef(startRow, startCol);
      const endCellRef = this.getCellRef(endRow, endCol);
      return `${startCellRef}:${endCellRef}`;
    },
    handleCellBlurEvent({ rowIndex, colIndex, value, formula }) {
      const cell = this.sheetData[rowIndex][colIndex];
      cell.value = value;
      cell.formula = formula || '';
    },
    handleCellInputEvent(value) {
      if (isSpecialCharAtEnd(value)) {
        this.setAllowToAddRefInFormula(true);
      }
      this.$set(this.activeCell, 'innerText', value);
    },
    handleCellFocusEvent() {
      if (this.activeCell) this.$set(this.activeCell, 'isFocus', true);
    },
    handleCellDoubleclick() {
      this.focusActiveCell();
      this.setNotExistCellDirect(true);
    },
    handleActiveCellclick() {
      this.setNotExistCellDirect(true);
      this.setAllowToAddRefInFormula(false);
    },
    convertFormulaLanguage(formula, direction = 'toEnglish') {
      try {
        if (!formula || typeof formula !== 'string') return formula;

        let converted = formula.trim();

        this.formulafunctionList.forEach((func) => {
          const from = direction === 'toEnglish' ? func.german : func.english;
          const to = direction === 'toEnglish' ? func.english : func.german;

          const regex = new RegExp(`\\b${from}\\b`, 'gi');
          converted = converted.replace(regex, to);
        });

        return converted;
      } catch (error) {
        return formula;
      }
    },
    isActiveCell(rowIndex, colIndex) {
      return (
        this.activeCellPosition.rowIndex === rowIndex &&
        this.activeCellPosition.colIndex === colIndex
      );
    },
    saveSelectRange() {
      if (!this.selectedRange.start || !this.selectedRange.end) return;
      const range = this.getRangeRefs(
        this.selectedRange.start,
        this.selectedRange.end
      );
      if (range) this.saveSelectCell(range);
    },
    saveSelectCell(cellRef) {
      if (!cellRef || this.isValidActiveCellText) return;
      if (this.selectedOutputCellList.includes(cellRef)) {
        this.selectedOutputCellList = this.selectedOutputCellList.filter(
          (cell) => cell !== cellRef
        );
      } else {
        this.selectedOutputCellList.push(cellRef);
      }
    },
    clearActiveCell() {
      this.activeCellPosition = { rowIndex: null, colIndex: null };
      this.activeCell = null;
    },
    setNotExistCellDirect(value) {
      this.isNotExistCellDirect = value;
    },
    setAllowToAddRefInFormula(value) {
      this.isAllowToAddRefInFormula = value;
    },
    focusActiveCell(isTooglePopover = true) {
      this.handleCellFocusEvent();
      this.$nextTick(() => {
        const targetCell = this.$refs.activeCellRef;
        if (targetCell?.$refs?.cell) {
          this.activeCell.innerText = targetCell.$refs.cell.innerText;

          setCursorAtLast(targetCell.$refs.cell);
        }
      });
      if (isTooglePopover) this.togglePopoverDropDown();
    },
    setActiveCell(rowIndex, colIndex) {
      this.activeCellPosition = { rowIndex, colIndex };
      const cell = this.sheetData[rowIndex][colIndex];
      this.activeCell = { ...cell, innerText: '', isFocus: false };
    },
    isFormulaEndingValid() {
      const innerText = this.activeCell?.innerText?.trim() || '';

      if (typeof innerText !== 'string') return false;

      const trimmed = innerText.trim();

      if (!trimmed.startsWith('=')) return false;

      if (trimmed === '=') return true;

      const validEndChars = ['+', '=', ',', '/', '%', '^', '*', '(', '-', ';'];
      const lastChar = trimmed.slice(-1);
      if (validEndChars.includes(lastChar)) return true;
      const cellRefPattern = /([A-Z]+\d+)(:[A-Z]+\d+)?$/i;
      return cellRefPattern.test(trimmed);
    },
    insertIntoActiveFormula(text, isAppend = false, isReplace = false) {
      if (
        ((!this.isValidActiveCellText || !text) && !isReplace) ||
        this.isMarkOutputMode
      )
        return;
      const innerText = this.activeCell?.innerText?.trim() || '';
      const targetCell = this.$refs.activeCellRef;
      if (targetCell?.appendToFormula) {
        targetCell.appendToFormula(
          text,
          isAppend || innerText === '=',
          isReplace
        );
      }
    },
    togglePopoverDropDown() {
      this.$nextTick(() => {
        const popover = this.$refs.popoverRef;
        if (!popover) return;
        popover.doClose();
        setTimeout(() => {
          const popover = this.$refs.popoverRef;
          popover.doOpen();
        }, 1);
      });
    },
    onInsertFormulaFunction(germanName) {
      let current = this.activeCell?.innerText?.trim()?.toUpperCase() || '';
      let cellText = null;
      if (!current || current === '=') {
        cellText = `=${germanName}(`;
      } else {
        const match =
          current.match(/^=([A-Z]*)$/) || current.match(/^=([A-Z]+)\(?/);
        const partial = match ? match[1] : '';

        cellText =
          current.replace(partial, germanName) +
          (current.endsWith('(') ? '' : '(');
      }
      if (cellText) this.insertIntoActiveFormula(cellText, false, true);
      this.focusActiveCell(false);
    },
    onCellMouseDown(rowIndex, colIndex) {
      try {
        this.isMouseDown = true;
        this.unSelectAllCell();
        if (
          this.keyModifiers.shift &&
          !this.isValidActiveCellText &&
          this.activeCell
        ) {
          this.$set(this.activeCell, 'isFocus', false);
        }
        if (this.isActiveCell(rowIndex, colIndex)) return;
        const cellRef = this.getCellRef(rowIndex, colIndex);

        if (this.keyModifiers.control) {
          if (!this.isMarkOutputMode) {
            this.setActiveCell(rowIndex, colIndex);
          }
          this.selectedRange.start = { rowIndex, colIndex };
          this.selectedRange.end = { rowIndex, colIndex };
          this.savedSelectedRange.start = { rowIndex, colIndex };
          this.savedSelectedRange.end = null;
          if (this.isValidActiveCellText)
            this.insertIntoActiveFormula(cellRef, true);
        } else if (this.keyModifiers.shift) {
          if (this.selectedRange.start) return;
          if (this.isMarkOutputMode) {
            this.selectedRange.start = { rowIndex, colIndex };
            this.selectedRange.end = { rowIndex, colIndex };
          } else {
            if (this.savedSelectedRange.start) {
              if (!this.isMarkOutputMode) this.selectedOutputCellList = [];

              this.selectedRange.start = { ...this.savedSelectedRange.start };
            } else {
              const innerText = this.activeCell?.innerText?.trim() || '';
              const lastCell = getLastCellReference(innerText);
              if (lastCell && lastCell.hasOwnProperty('start')) {
                this.selectedRange.start = lastCell.start;
              } else if (
                lastCell &&
                lastCell.hasOwnProperty('rowIndex') &&
                lastCell.hasOwnProperty('colIndex')
              ) {
                this.selectedRange.start = lastCell;
              } else if (
                this.activeCellPosition.rowIndex !== null &&
                this.activeCellPosition.colIndex !== null
              ) {
                this.selectedRange.start = { ...this.activeCellPosition };
              } else {
                this.selectedRange.start = { rowIndex, colIndex };
              }
            }
            this.savedSelectedRange.start = { ...this.selectedRange.start };
            this.selectedRange.end = { rowIndex, colIndex };
          }
          const range = this.getRangeRefs(
            this.selectedRange.start,
            this.selectedRange.end
          );
          if (range) this.insertIntoActiveFormula(range);
        } else if (this.isValidActiveCellText) {
          this.insertIntoActiveFormula(cellRef);
          this.savedSelectedRange = { start: null, end: null };
        } else {
          if (this.isMarkOutputMode) {
            this.saveSelectCell(cellRef);
          } else {
            this.savedSelectedRange = { start: null, end: null };
            this.selectedRange = { start: null, end: null };
            this.selectedOutputCellList = [];
            this.setActiveCell(rowIndex, colIndex);
          }
        }
      } catch (error) {
        console.log();
      }
    },
    onCellMouseOver(rowIndex, colIndex) {
      if (
        (this.keyModifiers.shift || this.keyModifiers.control) &&
        this.selectedRange.start &&
        this.isMouseDown
      ) {
        this.selectedRange.end = { rowIndex, colIndex };
        const range = this.getRangeRefs(
          this.selectedRange.start,
          this.selectedRange.end
        );
        if (range) this.insertIntoActiveFormula(range);
      }
    },
    onCellMouseUP() {
      this.isMouseDown = false;

      if (this.keyModifiers.shift || this.keyModifiers.control) {
        const range = this.getRangeRefs(
          this.selectedRange.start,
          this.selectedRange.end
        );
        if (range) {
          const parts = range.split(':');
          if (parts[0] === parts[1]) {
            this.saveSelectCell(parts[0]);
          } else {
            this.saveSelectRange();
          }
        }
        this.savedSelectedRange.end = this.selectedRange.end;
        this.selectedRange = { start: null, end: null };
      }
    },

    onEnterPress() {
      if (this.activeCell && !this.activeCell?.isFocus) {
        this.focusActiveCell();
        this.setNotExistCellDirect(true);
      } else {
        this.setNotExistCellDirect(false);
        this.insertIntoActiveFormula(')', true);
        this.handleArrowKeyNavigation('ArrowDown');
      }
      this.unSelectAllCell();
      this.setAllowToAddRefInFormula(false);
      this.selectedRange = { start: null, end: null };
      this.savedSelectedRange = { start: null, end: null };
    },
    handleTabKeyNavigation() {
      if (
        this.filteredFunctions?.length &&
        this.filteredFunctions.length < this.formulafunctionList.length
      ) {
        const selectedFunc = this.filteredFunctions[0];
        this.onInsertFormulaFunction(selectedFunc.german);
      } else {
        this.setNotExistCellDirect(false);
        this.insertIntoActiveFormula(')', true);
        this.handleArrowKeyNavigation('ArrowRight');
        this.setAllowToAddRefInFormula(false);
        this.selectedRange = { start: null, end: null };
        this.savedSelectedRange = { start: null, end: null };
      }
    },
    handleArrowKeyNavigation(pressKey) {
      this.unSelectAllCell();
      this.selectedRange = { start: null, end: null };
      this.savedSelectedRange = { start: null, end: null };
      this.selectedOutputCellList = [];

      if (this.isMarkOutputMode) return null;

      const navigateCellPosition = getNavigateCellCoordinate(
        pressKey,
        this.activeCellPosition,
        this.sheetDimensions
      );
      if (navigateCellPosition)
        this.setActiveCell(
          navigateCellPosition.rowIndex,
          navigateCellPosition.colIndex
        );
      this.$nextTick(() => {
        this.scrollCellIntoView(
          navigateCellPosition.rowIndex,
          navigateCellPosition.colIndex
        );
      });
    },
    setNavigateCellInformula(pressKey) {
      this.unSelectAllCell();
      if (this.isMarkOutputMode) return null;
      const innerText = this.activeCell?.innerText?.trim() || '';
      const lastCell = getLastCellReference(innerText);
      if (lastCell === null && this.isValidActiveCellText) return;
      if (lastCell === '') {
        this.savedSelectedRange = { start: null, end: null };
      }
      if (this.keyModifiers.shift) {
        const rangeEndCell = this.savedSelectedRange?.end ||
          lastCell || { ...this.activeCellPosition };
        if (!this.savedSelectedRange.start) {
          this.savedSelectedRange.start = lastCell || {
            ...this.activeCellPosition,
          };
        }
        const rangeStartCell = this.savedSelectedRange.start;

        const updatedEndCellPosition = getNavigateCellCoordinate(
          pressKey,
          rangeEndCell,
          this.sheetDimensions
        );

        if (updatedEndCellPosition) {
          this.savedSelectedRange.end = updatedEndCellPosition;
          if (this.savedSelectedRange.start) {
            this.selectedOutputCellList = [];
          }
          this.selectedRange = { ...this.savedSelectedRange };
          const range = this.getRangeRefs(
            rangeStartCell,
            updatedEndCellPosition
          );
          this.scrollCellIntoView(
            updatedEndCellPosition.rowIndex,
            updatedEndCellPosition.colIndex
          );
          if (range) this.insertIntoActiveFormula(range);
        }
      } else {
        let rowIndex, colIndex;
        if (this.savedSelectedRange.start) {
          rowIndex = this.savedSelectedRange.start.rowIndex;
          colIndex = this.savedSelectedRange.start.colIndex;
          this.savedSelectedRange = { start: null, end: null };
        } else if (lastCell.hasOwnProperty('start')) {
          rowIndex = lastCell.start.rowIndex;
          colIndex = lastCell.start.colIndex;
        } else {
          rowIndex = lastCell.hasOwnProperty('rowIndex')
            ? lastCell.rowIndex
            : this.activeCellPosition.rowIndex;
          colIndex = lastCell.hasOwnProperty('colIndex')
            ? lastCell.colIndex
            : this.activeCellPosition.colIndex;
        }
        const navigateCellPosition = getNavigateCellCoordinate(
          pressKey,
          { rowIndex, colIndex },
          this.sheetDimensions
        );
        if (!navigateCellPosition) return;
        const cellRef = this.getCellRef(
          navigateCellPosition.rowIndex,
          navigateCellPosition.colIndex
        );
        this.scrollCellIntoView(
          navigateCellPosition.rowIndex,
          navigateCellPosition.colIndex
        );
        this.insertIntoActiveFormula(cellRef);
      }
    },
    scrollCellIntoView(rowIndex, colIndex) {
      try {
        const container = this.$refs.sheetContainer;
        const cell = document.getElementById(`cell-${rowIndex}-${colIndex}`);
        if (!cell || !container) return;

        const extraLeft = 50;
        const extraTop = 24;

        const offsetLeft = cell.offsetLeft;
        const offsetTop = cell.offsetTop;
        const cellRight = offsetLeft + cell.offsetWidth;
        const cellBottom = offsetTop + cell.offsetHeight;

        if (offsetLeft < container.scrollLeft || offsetLeft < 100) {
          // Scrolling left
          container.scrollLeft = Math.max(offsetLeft - extraLeft, 0);
        } else if (cellRight > container.scrollLeft + container.clientWidth) {
          // Scrolling right
          container.scrollLeft = cellRight - container.clientWidth;
        }
        if (offsetTop < container.scrollTop + 25) {
          // Scrolling up
          container.scrollTop = Math.max(offsetTop - extraTop, 0);
        } else if (cellBottom > container.scrollTop + container.clientHeight) {
          // Scrolling down
          container.scrollTop = cellBottom - container.clientHeight;
        }
      } catch (error) {
        console.log('');
      }
    },
    handleKeyDown(event) {
      if (this.isShowClearAllConfirm) {
        event.preventDefault();
        return;
      }
      const isActiveFieldFocus = this.activeCell?.isFocus;
      if (event.key === 'Backspace') {
        this.setAllowToAddRefInFormula(false);
      }
      if (
        event.key === 'Delete' &&
        !this.isDisableClearButton &&
        !isActiveFieldFocus
      ) {
        this.toggleClearAllConfirm();
      } else if (
        (event.ctrlKey || event.metaKey) &&
        event.key.toLowerCase() === 'a' &&
        !isActiveFieldFocus &&
        !this.isMarkOutputMode
      ) {
        event.preventDefault();
        this.onSelectAll();
      } else if (
        event.key === 'Tab' &&
        this.activeCell &&
        !this.isMarkOutputMode
      ) {
        event.preventDefault();
        this.handleTabKeyNavigation(event.key);
      } else if (
        ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(
          event.key
        ) &&
        !this.isMarkOutputMode
      ) {
        if (this.isValidActiveCellText) {
          event.preventDefault();
        }

        if (
          this.activeCell &&
          (!isActiveFieldFocus ||
            (!this.isActiveCellStartWithEqual && !this.isNotExistCellDirect)) &&
          !this.keyModifiers.shift
        ) {
          this.handleArrowKeyNavigation(event.key);
        } else if (
          this.isValidActiveCellText ||
          (this.keyModifiers.shift && !this.isValidActiveCellText)
        ) {
          if (this.isNotExistCellDirect && !this.isValidActiveCellText) {
            return;
          }
          this.setNavigateCellInformula(event.key);
          if (!this.isValidActiveCellText && this.activeCell) {
            this.$set(this.activeCell, 'isFocus', false);
          }
        }
      } else if (event.key === 'Shift') {
        this.keyModifiers.shift = true;
      } else if (event.key === 'Control' || event.metaKey) {
        this.keyModifiers.control = true;
      } else if (
        event.key === 'Enter' &&
        this.activeCell &&
        !this.isMarkOutputMode
      ) {
        event.preventDefault();
        this.onEnterPress();
      } else if (!isActiveFieldFocus && this.activeCell) {
        this.activeCell = {
          formula: '',
          value: '',
          innerText: '',
          isFocus: false,
        };
        this.setNotExistCellDirect(false);
        this.focusActiveCell();
      }
    },
    handleKeyUp(event) {
      if (event.key === 'Shift') {
        this.keyModifiers.shift = false;
        const range = this.getRangeRefs(
          this.selectedRange.start,
          this.selectedRange.end
        );
        if (range) {
          const parts = range.split(':');
          if (parts[0] === parts[1]) {
            this.saveSelectCell(parts[0]);
          } else {
            this.saveSelectRange();
          }
        }
        this.selectedRange = { start: null, end: null };
      } else if (event.key === 'Control' || event.metaKey) {
        this.keyModifiers.control = false;
      }
    },
  },
  computed: {
    isActiveCellStartWithEqual() {
      const innerText = this.activeCell?.innerText?.trim() || '';
      return (
        this.activeCell &&
        innerText.startsWith('=') &&
        this?.activeCell?.isFocus
      );
    },
    isActiveCellReadyToAppend() {
      return this.isActiveCellStartWithEqual && this.isAllowToAddRefInFormula;
    },
    isDisableClearButton() {
      return (
        (!this.isSelectedAllCell &&
          (this.isMarkOutputMode || !this.selectedOutputCellList.length)) ||
        this.isMarkOutputMode
      );
    },
    isValidActiveCellText() {
      const innerText = this.activeCell?.innerText?.trim() || '';
      const lastCellRef = getLastCellReference(innerText);
      return lastCellRef !== null && this.isAllowToAddRefInFormula;
    },
    sheetDimensions() {
      return {
        rows: this.task?.props?.rows || 25,
        cols: this.task?.props?.cols || 26,
      };
    },
    activeCellBoxStyle() {
      if (
        this.activeCellPosition.rowIndex === null ||
        this.activeCellPosition.colIndex === null
      )
        return null;
      const top = this.activeCellPosition.rowIndex * this.cellHeight + 24;
      const left = this.activeCellPosition.colIndex * this.cellWidth + 50;

      return {
        top: `${top}px`,
        left: `${left}px`,
        'min-width': `${this.cellWidth}px`,
        height: `${this.cellHeight}px`,
      };
    },
    currentRangeBoxStyle() {
      if (
        !this.selectedRange.start ||
        !this.selectedRange.end ||
        !(this.isMarkOutputMode || !this.isValidActiveCellText)
      )
        return null;
      return getCellHighlightBoxStyle(
        this.selectedRange.start,
        this.selectedRange.end,
        this.cellWidth,
        this.cellHeight
      );
    },
    selectedBoxesStyle() {
      let selectedRefs = [];
      if (this.isMarkOutputMode) {
        selectedRefs = this.selectedOutputCellList;
      } else if (this.activeCell && this.isActiveCellStartWithEqual) {
        const innerText = this.activeCell?.innerText?.trim() || '';
        const parseCellRef = extractCellRefsFromFormula(innerText);
        selectedRefs = [...parseCellRef.cellRefs, ...parseCellRef.rangeRefs];
      } else {
        selectedRefs = this.selectedOutputCellList;
      }

      return selectedRefs.map((r) => {
        const { start, end } = parseRange(r);
        return getCellHighlightBoxStyle(
          start,
          end,
          this.cellWidth,
          this.cellHeight
        );
      });
    },
    filteredFunctions() {
      const text = this.activeCell?.innerText?.trim()?.toUpperCase() || '';

      if (!text.startsWith('=')) return [];
      if (text === '=') return this.formulafunctionList;

      if (/[A-Z]+\s*\(/.test(text)) return [];

      const match = text.match(/^=([A-Z]*)$/);
      if (!match) [];
      const partial = match?.[1] || '';

      if (!partial) return [];

      return this.formulafunctionList.filter((func) =>
        func.german.startsWith(partial)
      );
    },
    outputModebuttonLabel() {
      return this.isMarkOutputMode ? 'Save Output' : 'Mark Output';
    },
  },
  mounted() {
    this.initGrid(this.task?.props?.sheetData);

    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
  },
  components: { SpreadSheetCell, LoadingSpinner, CommonModal },
};
</script>
<style scoped>
@import 'https://use.fontawesome.com/releases/v5.6.3/css/all.css';

.matrix-editor-task {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  user-select: none;
}

.matrix-editor-task .custom-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between !important;
  flex-direction: column;
  gap: 10px;
  margin-left: auto;
  padding: 10px 0;
  max-width: 100%;
}
.matrix-editor-task .custom-modal-header h2 {
  margin: 0;
  padding: 0;
}
.matrix-editor-task .custom-modal-header .action-buttons-wrapper {
  display: flex;
  gap: 20px;
  align-items: center;
  justify-content: center;
  max-width: 53%;
  margin-left: auto;
}
.matrix-editor-task .modal.task {
  padding: 30px 94px;
}
.matrix-editor-task .custom-modal-header .action-buttons-wrapper button {
  border-radius: 5px;
  height: 35px;
  width: 100%;
  min-width: 120px;
  display: inline-block;
  border: 1px solid #000000;
  background: #000000;
  cursor: pointer;
  line-height: normal;
  font-size: 14px;
  transition: all 0.2s ease-in-out;
}
.matrix-editor-task .custom-modal-header .action-buttons-wrapper button span,
.matrix-editor-task .custom-modal-header .action-buttons-wrapper button {
  font-size: 14px;
  text-align: center;
  white-space: nowrap;
  color: #ffffff;
  letter-spacing: normal;
  line-height: normal;
  transition: all 0.2s ease-in-out;
}

.matrix-editor-task
  .custom-modal-header
  .action-buttons-wrapper
  button:disabled {
  background-color: gray;
  border-color: gray;
}

.matrix-editor-task
  .custom-modal-header
  .action-buttons-wrapper
  button:hover:not(:disabled) {
  background-color: transparent;
  color: #000000;
}
.matrix-editor-task
  .custom-modal-header
  .action-buttons-wrapper
  button:hover
  span {
  color: #000000;
}

.matrix-editor-task .custom-modal-header button .progress-icon {
  content: ' ';
  display: block;
  width: 25px;
  height: 25px;
  margin: 1px;
  border-radius: 50%;
  border: 2px solid #fff;
  border-color: #fff transparent #fff transparent;
  animation: progress-icon-animation 1.2s linear infinite;
  margin: 0 auto;
}
.matrix-editor-task .custom-modal-header button:hover .progress-icon {
  border: 2px solid #000000;
  border-color: #000000 transparent #000000 transparent;
}

@keyframes progress-icon-animation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
.matrix-editor-task .top-layout {
  position: sticky;
  top: 0;
  z-index: 1;
}
.matrix-editor-task .top-layout .alpha-item {
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.matrix-editor-task .top-layout,
.matrix-editor-task .bottom-layout {
  display: flex;
  width: max-content;
}
.matrix-editor-task .bottom-layout {
  position: relative;
}
.matrix-editor-task .numaric-wrapper {
  min-width: 50px;
  border-right: 1px solid #c4c7c5;
  position: sticky;
  left: 0;
  background-color: #ffffff;
}
.matrix-editor-task .numaric-wrapper > div {
  min-height: 22px;
  border-top: 1px solid #c4c7c5;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
}
.matrix-editor-task .alpha-item {
  flex: 1;
  min-height: 22px;
  border-top: 1px solid #c4c7c5;
  padding: 0 3px;
  min-width: 120px;
  max-width: 120px;
}

.matrix-editor-task .main-layout {
  border: 1px solid #c4c7c5;
  border-top: 0;
  overflow: auto;
  height: calc(100% - 100px);
  padding-bottom: 2px;
}
.bottom-layout:has(.alpha-item [contenteditable]:focus) {
  position: relative;
  z-index: 1;
}

.matrix-editor-task .top-layout .numaric-wrapper {
  border-top: 1px solid #c4c7c5;
  border-right: 1px solid #c4c7c5;
}

.matrix-editor-task .alpha-item:not(:last-child) {
  border-right: 1px solid #c4c7c5;
}

.matrix-editor-task .content-wrapper,
.matrix-editor-task .alpha-wrapper {
  flex: 1;
  flex-direction: column;
  min-width: 1900px;
}
.matrix-editor-task .content-wrapper {
  border-top: 0;
}
.matrix-editor-task .alpha-wrapper {
  display: flex;
  flex-direction: row;
}
.matrix-editor-task .content-row {
  display: flex;
}

.matrix-editor-task .selected-all .content-row:first-child .alpha-item,
.matrix-editor-task .selected-all .content-row .content-count-item {
  background-color: #1a73e8;
  color: #ffffff;
}
.matrix-editor-task .selected-all .content-row .alpha-item {
  background-color: rgba(11, 87, 208, 0.1);
}

.matrix-editor-task ::v-deep .alpha-item [contenteditable] {
  outline: none;
}
.matrix-editor-task .active-cell-box [contenteditable] {
  outline: none;
}
.matrix-editor-task .alpha-item .input-cell {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
  cursor: default;
}

.selected-cell {
  background-color: rgba(0, 112, 255, 0.1);
  box-shadow: inset 0 0 0 2px #56af45;
}
.matrix-editor-task .content-row .content-count-item {
  min-height: 22px;
  border-top: 1px solid #c4c7c5;
  background: #ffffff;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  min-width: 50px;
  border-right: 1px solid #c4c7c5;
  position: sticky;
  left: 0;
  z-index: 9;
}

.matrix-editor-task .content-row:first-child {
  position: sticky;
  top: 0;
  background-color: #ffffff;
  border-bottom: 1px solid #c4c7c5;
  border-top: 1px solid #c4c7c5;
  z-index: 10;
}
.matrix-editor-task .content-row:first-child .alpha-item {
  border-top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.matrix-editor-task .content-row:first-child div:first-child {
  border-top: 0;
}

.matrix-editor-task .main-layout .bottom-layout .highlight-box {
  position: absolute;
  border: 1px solid #1a73e8;
  background-color: rgba(11, 87, 208, 0.1);
  pointer-events: none;
  box-sizing: border-box;
  z-index: 6;
}

.matrix-editor-task .main-layout .bottom-layout .highlight-box.output-box {
  border: 1px solid #0fbf00;
  background-color: rgba(15, 191, 0, 0.2);
  z-index: 6;
}
.matrix-editor-task .main-layout .bottom-layout .active-cell-box {
  background-color: transparent !important;
  pointer-events: unset;
  border-width: 2px;
  padding: 0 3px;
}
.matrix-editor-task .main-layout .bottom-layout .active-cell-box.is-focus {
  background-color: #ffffff !important;
  outline: 2px solid #abc7ea;
}

.matrix-editor-task .loading-container {
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 99;
}

/* popover dropdown style */
.matrix-editor-task ::v-deep .formula-popover-dropdown {
  box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
  border-radius: 3px;
  z-index: 9 !important;
}
.matrix-editor-task ::v-deep .formula-popover-dropdown .popover-body {
  margin: 0 !important;
  width: 130px;
  padding: 5px 0;
}

.matrix-editor-task
  ::v-deep
  .formula-popover-dropdown:not(:has(.list-view))
  .popover-body {
  display: none;
}
.matrix-editor-task ::v-deep .formula-popover-dropdown .popover-body ul {
  background: #ffffff;
  list-style: none;
  color: #333;
  padding: 0;
  margin: 0;
  text-align: initial;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
}
.matrix-editor-task ::v-deep .formula-popover-dropdown .popover-body ul li {
  cursor: pointer;
  font-size: 14px;
  padding: 5px 7px;
  width: 100%;
}
</style>
